import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "pinterest",
  app: "Pinterest",
  title: "How to block Pinterest on iPhone — planning as a hobby",
  h1: "How to block Pinterest",
  description:
    "Screen Time steps, why Pinterest feels productive while consuming an evening, and the difference between planning something and preparing for it.",
  targets: [
    "how to block pinterest on iphone",
    "stop scrolling pinterest",
    "pinterest time waster",
    "how to limit pinterest",
  ],
  published: "2026-09-03",
  standfirst:
    "Pinterest is the only app on this list that leaves people feeling organised. The board is not the kitchen, and the evening is still gone.",
  takeaways: [
    "The feeling of productivity is the mechanic. Nothing else here disguises itself as preparation.",
    "Set a limit on the whole Entertainment or Shopping category rather than the app, because the substitute is a different browsing app rather than a different feed.",
    "A useful test: has anything on a board from a year ago been built, cooked, bought or worn?",
  ],
  mechanic: [
    "Pinterest presents itself as a tool. You are planning a kitchen, a wedding, a garden, a wardrobe — and the activity has the shape of work: choosing, sorting, filing into boards. That framing is the mechanic, because it removes the only signal most people have that a session has gone on too long, which is the faint discomfort of having consumed something pointless.",
    "Underneath the framing it is an infinite grid with no end state. Every image opens into more like it, the grid reloads continuously, and there is no moment at which a board is finished — a board is a collection, and collections are open-ended by definition. So the session ends when you are tired rather than when you have what you came for.",
    "And what is being collected is mostly aspiration rather than instruction. Pinterest's inventory skews heavily towards the photographed result rather than the method, which means an hour there frequently produces a stronger sense of what your kitchen should look like and no additional ability to change it — the exact combination that produces dissatisfaction rather than a plan.",
  ],
  limitFails:
    "The limit fires and it feels unreasonable, because you were doing something — the wedding is real, the room is real, the list is real. That legitimacy makes the dismissal feel like good judgement rather than weakness, which is a stronger defeat for a limit than temptation is. And Pinterest's usage tends to arrive in bursts around a project, so a fixed daily allowance is either irrelevant for weeks or wrong on precisely the days it matters.",
  instead: [
    "Give the project a time box rather than the app a daily limit: an hour on Saturday for the kitchen, and none the rest of the week.",
    "Set the limit on the whole category so the browsing does not simply relocate to a shopping app.",
    "Decide what you are looking for before opening it, and close it when you have found that. The absence of a target is what makes the grid infinite.",
    "Prune the boards once. Most people discover the top of the list is two years old, which answers the question of whether this is planning.",
  ],
  verses: [
    {
      verse: {
        text: "The life of the body is a heart at peace, but envy rots the bones.",
        ref: "Proverbs 14:30",
      },
      note: "Slow and physical, and the mechanism is rot rather than fire — invisible from outside, working over a long time. Pinterest is not usually experienced as envy, which is exactly what makes this the right verse for it: an hour of other people's finished rooms is a slow comparison, and the discontent it produces arrives later and unattributed.",
    },
    {
      verse: {
        text: "But let each man examine his own work, and then he will have reason to boast in himself, and not in someone else.",
        ref: "Galatians 6:4",
      },
      note: "The remedy offered is a change of measuring stick rather than a moral effort: look at your actual work. On an app built entirely from other people's finished results, that instruction is unusually concrete — go and look at the room you actually have, and assess that rather than the difference between it and a photograph.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Planning is not preparing",
      id: "planning-is-not-preparing",
    },
    {
      type: "p",
      text: "There is a real category of activity that feels like progress and produces none: researching the thing, collecting references for the thing, reading about people who did the thing. It is more comfortable than the thing, and Pinterest is the most refined version of it ever built.",
    },
    {
      type: "p",
      text: "The test is whether anything leaves the board. If nothing has in a year, the boards are not a plan; they are the activity.",
    },
    {
      type: "h2",
      text: "Time-box the project",
      id: "time-box-the-project",
    },
    {
      type: "p",
      text: "Because the use is legitimate and bursty, a daily allowance is the wrong shape. An hour on Saturday attached to a specific decision does what a thirty-minute daily budget cannot: it ends when the decision is made.",
    },
    {
      type: "note",
      text: "Write the decision down before you open it. One kitchen worktop, one dress, one shelf. The grid is infinite; the question does not have to be.",
    },
    {
      type: "h2",
      text: "The quiet cost",
      id: "the-quiet-cost",
    },
    {
      type: "p",
      text: "Nobody finishes a Pinterest session feeling ashamed, and that is why the cost is easy to miss. What is left afterwards is a slightly sharper sense of the gap between the house you have and the houses you have been looking at.",
    },
    {
      type: "p",
      text: "That is the same injury {the comparison problem} describes elsewhere on this site, arriving through a door nobody guards.",
      links: {
        "the comparison problem": "/verses/bible-verses-about-comparison",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Pinterest on my iPhone?",
      a: "Screen Time → App Limits → Add Limit, and choose the whole category rather than Pinterest alone, with Block at End of Limit and a passcode. For a live project, a weekly time box works better than a daily allowance.",
    },
    {
      q: "Is Pinterest actually a waste of time?",
      a: "Not inherently — it is genuinely useful when you arrive with a decision to make. The problem is the sessions with no target, which feel like planning and produce a collection rather than a kitchen.",
    },
    {
      q: "Why do I feel worse after using Pinterest?",
      a: "Because an hour of other people's finished results sharpens the gap between what you have and what you have been looking at. It is comparison arriving through a door nobody thinks to guard.",
    },
  ],
  relatedApps: ["instagram", "facebook"],
  related: ["social-media-fast-guide", "dopamine-detox-christian"],
  relatedPrayers: ["prayer-for-comparison", "prayer-for-gratitude"],
};
